import { useEffect, useState } from "react";

import { getMe, getVersion, type Me } from "@/lib/api";
import { useLocation } from "@/lib/useLocation";
import { AdminPage } from "@/pages/AdminPage";
import { Dashboard } from "@/Dashboard";
import { LandingPage } from "@/LandingPage";
import "./index.css";

// LoadingSplash covers the window between mount and the first /api/me
// resolving. Deliberately neutral (no "taco" hero) so it reads as a plain
// loading state in BOTH directions — a logged-in user briefly sees it then
// the dashboard, a logged-out MT user sees it then the landing page. This
// gate is what prevents a landing-page flash for authenticated users: the
// routing below must check `me === undefined` BEFORE the landing branch.
function LoadingSplash() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="size-6 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
    </div>
  );
}

export function App() {
  const { pathname, navigate, replace } = useLocation();
  const onAdminPath = pathname === "/admin";

  // Auth identity. `undefined` = not fetched yet (show the splash);
  // `null` = fetch failed, treated like legacy (fall through to the
  // dashboard); otherwise the resolved /api/me payload. /api/me is a
  // public probe (legacy and MT both return 200), so this never trips the
  // global 401 redirect in request().
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  useEffect(() => {
    void getMe()
      .then(setMe)
      .catch(() => setMe(null));
  }, []);

  // Release tag from /api/version, rendered inconspicuously by the
  // dashboard/landing footers. /api/version is also a public probe in MT,
  // so the fetch is safe before login. Fails silently.
  const [version, setVersion] = useState<string>("");
  useEffect(() => {
    void getVersion()
      .then((v) => setVersion(v.version || "dev"))
      .catch(() => setVersion(""));
  }, []);

  // Non-admin users who land on /admin get bounced back to the root.
  // Client-side only — the per-endpoint isPlatformAdmin checks on the
  // server are the real safety line; this just prevents the blank
  // admin shell from flashing. Uses replace (not push) so Back
  // doesn't land back on /admin.
  useEffect(() => {
    if (onAdminPath && me && me.authenticated && !me.platform_admin) {
      replace("/");
    }
  }, [onAdminPath, me, replace]);

  // Routing. Order matters: the splash check must precede the landing
  // check so logged-in users never flash the landing page.
  if (me === undefined) {
    return <LoadingSplash />;
  }

  // Logged-out in multi-tenant mode → public landing page. Legacy mode
  // (auth_enabled falsy) and authenticated users skip this branch.
  if (me && me.auth_enabled && !me.authenticated) {
    return <LandingPage version={version} />;
  }

  if (onAdminPath && me?.platform_admin) {
    return (
      <>
        <AdminPage onBack={() => navigate("/")} />
        {version && (
          <div
            className="pointer-events-none fixed bottom-2 left-3 select-none font-mono text-[10px] text-muted-foreground/40"
            title="taco build"
          >
            {version}
          </div>
        )}
      </>
    );
  }

  return <Dashboard me={me} version={version} navigate={navigate} />;
}

export default App;
